import type { Gesamtkatalog, KatalogAbschnitt, KatalogEintrag } from '../typen';

/**
 * Normalisiert den Gesamtkatalog (gesamtkatalog.json). Die Datei kommt per
 * `import()` (laden.ts) und wird als `unknown` behandelt: ein kaputter oder
 * veralteter Chunk darf die Suche leeren, aber nie werfen.
 *
 * Übernommen werden nur die Felder des Vertrags (id, abschnitt, titel, text,
 * vorschrift) — alles andere fällt weg, auch wenn es in der Datei stünde.
 */

const text = (v: unknown): string => (typeof v === 'string' ? v : '');

const abschnittLesen = (roh: unknown): KatalogAbschnitt | null => {
    if (!roh || typeof roh !== 'object') return null;
    const o = roh as Record<string, unknown>;
    const id = text(o.id).trim();
    if (!id) return null;
    const a: KatalogAbschnitt = { id, titel: text(o.titel) };
    const eltern = text(o.eltern).trim();
    if (eltern) a.eltern = eltern;
    return a;
};

const eintragLesen = (roh: unknown): KatalogEintrag | null => {
    if (!roh || typeof roh !== 'object') return null;
    const o = roh as Record<string, unknown>;
    const id = text(o.id).trim();
    if (!id) return null;
    return {
        id,
        abschnitt: text(o.abschnitt).trim(),
        titel: text(o.titel),
        text: text(o.text),
        vorschrift: text(o.vorschrift),
    };
};

/** Liefert einen gültigen Gesamtkatalog oder null (nie werfen). Ungültige Einträge fallen einzeln heraus. */
export function quelleNormalisieren(roh: unknown): Gesamtkatalog | null {
    if (!roh || typeof roh !== 'object') return null;
    const o = roh as Record<string, unknown>;
    if (o.format !== 1 || !Array.isArray(o.eintraege)) return null;
    const abschnitte = Array.isArray(o.abschnitte)
        ? o.abschnitte.map(abschnittLesen).filter((a): a is KatalogAbschnitt => a !== null)
        : [];
    const eintraege = o.eintraege.map(eintragLesen).filter((e): e is KatalogEintrag => e !== null);
    return { format: 1, abschnitte, eintraege };
}
